import ISupplierSettings from "../models/ISupplierSettings";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import IUserTypeAccessDetail from "../models/IUserTypeAccessDetail";
import INavItem from "../models/INavItem";
import INavSubItem from "../models/INavSubItem";
import MenuIdEnum from "../models/MenuIdEnum";
import IViewAccessDetailFnData from "../models/IViewAccessDetailFnData";
import ViewAccessService from "./ViewAccessService";
import UserTypesEnum from "../models/UserTypesEnum";
import IScreenUserTypes from "../models/IScreenUserTypes";
import ScreenOptionsEnum from "../models/ScreenOptionsEnum";

/**
 * Creates a nav item with automatic screen_options integration.
 *
 * This helper implements a unified access control pattern where:
 * 1. screen_options (admin-configured) take precedence when present
 * 2. ViewAccessService (code-defined) is used as fallback
 *
 * This provides backwards compatibility while allowing admins to override
 * default permissions through the EditScreenOptions UI.
 *
 * @param config - Menu item configuration including screenKey for screen_options lookup
 * @param user - Current user model
 * @param supplier - Current supplier model
 * @returns INavItem with viewAccessDetailFn that checks screen_options first
 *
 * @example
 * const catalogs = createNavItemWithScreenOptions({
 *     menuId: MenuIdEnum.CATALOGS,
 *     screenKey: 'catalogs',
 *     icon: "fad fa-books",
 *     path: "/catalogs",
 *     title: "catalogs",
 *     ariaLabel: "Catalogs",
 *     minUserLevel: SystemConst.userTypes.ADMIN,
 *     viewAccessDetail: ViewAccessService.Catalogs,
 * }, user, supplier);
 */
export const createNavItemWithScreenOptions = (
    config: {
        menuId: MenuIdEnum;
        screenKey: ScreenOptionsEnum;
        icon: string;
        path: string;
        title: string;
        ariaLabel: string;
        exact?: boolean;
        minUserLevel: number;
        viewAccessDetail: IUserTypeAccessDetail;
        sections?: INavSubItem[];
        selectable?: boolean;
        pathFn?: () => string;
    },
    user: IUserUI,
    supplier: ISupplierModel
): INavItem => {
    return {
        menuId: config.menuId,
        icon: config.icon,
        path: config.path,
        title: config.title,
        ariaLabel: config.ariaLabel,
        exact: config.exact ?? true,
        minUserLevel: config.minUserLevel,
        viewAccessDetail: config.viewAccessDetail,
        sections: config.sections,
        selectable: config.selectable,
        pathFn: config.pathFn,
        viewAccessDetailFn: (): IViewAccessDetailFnData => {
            // ADMIN users always have full access - cannot be restricted
            if (user.userType?.id === UserTypesEnum.ADMIN) {
                return {
                    useOverride: true,
                    userHasAccess: true,
                };
            }

            // Priority 1: Check screen_options if configured
            if (supplier.settings?.screen_options?.[config.screenKey] && user.userType?.id) {
                const screenOption = supplier.settings.screen_options[config.screenKey] as any;
                const userTypeId = user.userType.id as any;
                if (screenOption?.[userTypeId]?.READ !== undefined) {
                    // screen_options configured - use it (overrides ViewAccessService)
                    return {
                        useOverride: true,
                        userHasAccess: screenOption[userTypeId].READ || false,
                    };
                }
            }

            // Priority 2: Fallback to ViewAccessService default
            // When useOverride is false, the navigation system will use viewAccessDetail
            return {
                useOverride: false,
                userHasAccess: false, // Will use viewAccessDetail instead
            };
        },
    };
};

/**
 * Checks if user has EDIT access to a screen.
 *
 * Implements the same precedence pattern:
 * 1. screen_options (admin-configured) take precedence
 * 2. ViewAccessService (code-defined) used as fallback
 *
 * Use this in components to control visibility of edit buttons, forms, etc.
 *
 * @param screenKey - The screen_options key (e.g., 'catalogs', 'orders')
 * @param user - Current user model
 * @param supplier - Current supplier model
 * @param viewAccessDetail - ViewAccessService definition for fallback
 * @returns boolean - whether user has EDIT access
 *
 * @example
 * const canEdit = hasEditAccess(
 *     'catalogs',
 *     userModel,
 *     supplierModel,
 *     ViewAccessService.Catalogs
 * );
 *
 * return (
 *     <div>
 *         {canEdit && <button>Edit Catalog</button>}
 *     </div>
 * );
 */
export const hasEditAccess = (
    screenKey: ScreenOptionsEnum,
    user: IUserUI,
    supplier: ISupplierModel,
    viewAccessDetail: IUserTypeAccessDetail
): boolean => {
    // ADMIN users always have full EDIT access - cannot be restricted
    if (user.userType?.id === UserTypesEnum.ADMIN) {
        return true;
    }

    // Priority 1: Check screen_options if configured
    if (supplier.settings?.screen_options?.[screenKey] && user.userType?.id) {
        const screenOption = supplier.settings.screen_options[screenKey] as any;
        const userTypeId = user.userType.id as any;
        if (screenOption?.[userTypeId]?.EDIT !== undefined) {
            console.log("screenOption", screenOption, "userTypeId", userTypeId, "EDIT", screenOption[userTypeId].EDIT);
            return screenOption[userTypeId].EDIT || false;
        }
    }

    // Priority 2: Fallback to ViewAccessService
    if (user.userType?.id) {
        const userTypeId = user.userType.id as UserTypesEnum;

        if (userTypeId === UserTypesEnum.GLOBAL_ADMIN) {
            return true;
        }

        if (userTypeId === UserTypesEnum.GLOBAL_SUPPORT) {
            return true;
        }

        if (viewAccessDetail[userTypeId]?.EDIT !== undefined) {
            return viewAccessDetail[userTypeId].EDIT || false;
        }
    }

    return false;
};

/**
 * Checks if user has READ access to a screen.
 *
 * Implements the same precedence pattern:
 * 1. screen_options (admin-configured) take precedence
 * 2. ViewAccessService (code-defined) used as fallback
 *
 * Use this in components when you need to explicitly check READ access
 * (though menu visibility is typically handled by createNavItemWithScreenOptions).
 *
 * @param screenKey - The screen_options key (e.g., 'catalogs', 'orders')
 * @param user - Current user model
 * @param supplier - Current supplier model
 * @param viewAccessDetail - ViewAccessService definition for fallback
 * @returns boolean - whether user has READ access
 *
 * @example
 * const canView = hasReadAccess(
 *     'orders',
 *     userModel,
 *     supplierModel,
 *     ViewAccessService.Orders
 * );
 */
export const hasReadAccess = (
    screenKey: ScreenOptionsEnum,
    user: IUserUI,
    supplier: ISupplierModel,
    viewAccessDetail: IUserTypeAccessDetail
): boolean => {
    // ADMIN users always have full READ access - cannot be restricted
    if (user.userType?.id === UserTypesEnum.ADMIN) {
        return true;
    }

    // Priority 1: Check screen_options if configured
    if (supplier.settings?.screen_options?.[screenKey] && user.userType?.id) {
        const screenOption = supplier.settings.screen_options[screenKey] as any;
        const userTypeId = user.userType.id as any;
        if (screenOption?.[userTypeId]?.READ !== undefined) {
            return screenOption[userTypeId].READ || false;
        }
    }

    // Priority 2: Fallback to ViewAccessService
    if (user.userType?.id) {
        const userTypeId = user.userType.id as UserTypesEnum;

        if (userTypeId === UserTypesEnum.GLOBAL_ADMIN) {
            return true;
        }

        if (userTypeId === UserTypesEnum.GLOBAL_SUPPORT) {
            return true;
        }

        if (viewAccessDetail[userTypeId]?.VIEW !== undefined) {
            return viewAccessDetail[userTypeId].VIEW || false;
        }
    }

    return false;
};

/**
 * Gets the default screen_options structure for a screen from ViewAccessService.
 * Converts VIEW → READ and EDIT → EDIT to match screen_options structure.
 *
 * This provides the default permissions that will be used when screen_options
 * are not configured. Useful for resetting customizations back to defaults.
 *
 * @param screenKey - The screen_options key (e.g., 'orders', 'catalogs')
 * @returns IScreenUserTypes - Default permissions for all user types
 *
 * @example
 * const defaults = getDefaultScreenOptions('orders');
 * // Returns: { [UserTypesEnum.ADMIN]: { READ: true, EDIT: true }, ... }
 */
export const getDefaultScreenOptions = (screenKey: ScreenOptionsEnum): IScreenUserTypes => {
    // Map screen keys to ViewAccessService definitions
    const SCREEN_DEFAULTS_MAP: Record<ScreenOptionsEnum, IUserTypeAccessDetail> = {
        [ScreenOptionsEnum.ORDERS]: ViewAccessService.Orders,
        [ScreenOptionsEnum.CATALOGS]: ViewAccessService.Catalogs,
        [ScreenOptionsEnum.INVENTORY]: ViewAccessService.Inventory,
        [ScreenOptionsEnum.QUOTES]: ViewAccessService.Quotes,
        [ScreenOptionsEnum.SHIPPING]: ViewAccessService.Shipments,
        [ScreenOptionsEnum.PICK_LISTS]: ViewAccessService.Inventory,
        [ScreenOptionsEnum.PACKING_LISTS]: ViewAccessService.Inventory,
        [ScreenOptionsEnum.WAREHOUSES]: ViewAccessService.Inventory,
        [ScreenOptionsEnum.ACCOUNTS]: ViewAccessService.UsersVendors,
        [ScreenOptionsEnum.ACCOUNT_USERS]: ViewAccessService.UsersVendorsUsers,
        [ScreenOptionsEnum.USERS]: ViewAccessService.Users,
        [ScreenOptionsEnum.PURCHASE_ORDERS]: ViewAccessService.PurchaseOrders,
        [ScreenOptionsEnum.TEMPLATES]: ViewAccessService.Templates,
        [ScreenOptionsEnum.ORDER_ENTRY]: ViewAccessService.OrderEntry,
    };

    // Type assertion needed because TypeScript can't infer the union type correctly
    const viewAccessDetail = SCREEN_DEFAULTS_MAP[screenKey];

    if (!viewAccessDetail) {
        return {
            [UserTypesEnum.ADMIN]: {
                READ: false,
                EDIT: false,
            },
            [UserTypesEnum.SALES]: {
                READ: false,
                EDIT: false,
            },
            [UserTypesEnum.ORDER_MGMT]: {
                READ: false,
                EDIT: false,
            },
            [UserTypesEnum.SHIP_MGMT]: {
                READ: false,
                EDIT: false,
            },
            [UserTypesEnum.INVENTORY_MGMT]: {
                READ: false,
                EDIT: false,
            },
            [UserTypesEnum.SUPPORT]: {
                READ: false,
                EDIT: false,
            },
            [UserTypesEnum.MANAGEMENT]: {
                READ: false,
                EDIT: false,
            },
            [UserTypesEnum.DRIVER]: {
                READ: false,
                EDIT: false,
            },
            [UserTypesEnum.VENDOR_SOURCE_USER]: {
                READ: false,
                EDIT: false,
            },
        };
    }

    // Convert VIEW/EDIT to READ/EDIT format for screen_options
    const defaultOptions: IScreenUserTypes = {
        [UserTypesEnum.ADMIN]: {
            READ: viewAccessDetail[UserTypesEnum.ADMIN]?.VIEW || false,
            EDIT: viewAccessDetail[UserTypesEnum.ADMIN]?.EDIT || false,
        },
        [UserTypesEnum.SALES]: {
            READ: viewAccessDetail[UserTypesEnum.SALES]?.VIEW || false,
            EDIT: viewAccessDetail[UserTypesEnum.SALES]?.EDIT || false,
        },
        [UserTypesEnum.ORDER_MGMT]: {
            READ: viewAccessDetail[UserTypesEnum.ORDER_MGMT]?.VIEW || false,
            EDIT: viewAccessDetail[UserTypesEnum.ORDER_MGMT]?.EDIT || false,
        },
        [UserTypesEnum.SHIP_MGMT]: {
            READ: viewAccessDetail[UserTypesEnum.SHIP_MGMT]?.VIEW || false,
            EDIT: viewAccessDetail[UserTypesEnum.SHIP_MGMT]?.EDIT || false,
        },
        [UserTypesEnum.INVENTORY_MGMT]: {
            READ: viewAccessDetail[UserTypesEnum.INVENTORY_MGMT]?.VIEW || false,
            EDIT: viewAccessDetail[UserTypesEnum.INVENTORY_MGMT]?.EDIT || false,
        },
        [UserTypesEnum.SUPPORT]: {
            READ: viewAccessDetail[UserTypesEnum.SUPPORT]?.VIEW || false,
            EDIT: viewAccessDetail[UserTypesEnum.SUPPORT]?.EDIT || false,
        },
        [UserTypesEnum.MANAGEMENT]: {
            READ: viewAccessDetail[UserTypesEnum.MANAGEMENT]?.VIEW || false,
            EDIT: viewAccessDetail[UserTypesEnum.MANAGEMENT]?.EDIT || false,
        },
        [UserTypesEnum.DRIVER]: {
            READ: viewAccessDetail[UserTypesEnum.DRIVER]?.VIEW || false,
            EDIT: viewAccessDetail[UserTypesEnum.DRIVER]?.EDIT || false,
        },
        [UserTypesEnum.VENDOR_SOURCE_USER]: {
            READ: viewAccessDetail[UserTypesEnum.VENDOR_SOURCE_USER]?.VIEW || false,
            EDIT: viewAccessDetail[UserTypesEnum.VENDOR_SOURCE_USER]?.EDIT || false,
        },
    };

    return defaultOptions;
};

const ScreenOptionsService = {
    createNavItemWithScreenOptions,
    hasEditAccess,
    hasReadAccess,
    getDefaultScreenOptions,
};

export default ScreenOptionsService;
